import React from 'react';
import '../../css/from.css';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, Typography, Switch, Upload, Avatar, Row, Col } from 'antd';
import { PlusOutlined, UserOutlined, CameraOutlined, CloseOutlined, MinusCircleOutlined, GlobalOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { createAboutContent, updateAboutContent, selectAboutSubmitStatus, resetSubmitStatus } from '../../redux/aboutSlice';

const { TextArea } = Input;

const AboutForm = ({ initialValues, onSuccess }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const submitStatus = useSelector(selectAboutSubmitStatus);

  const [previews, setPreviews] = React.useState({
    director: null,
    deputy_director: null,
    assistant: null,
    coordinator: null
  });
  const [imageFiles, setImageFiles] = React.useState({
    director: null,
    deputy_director: null,
    assistant: null,
    coordinator: null
  });

  const isEditing = Boolean(initialValues?.id);

  React.useEffect(() => {
    form.resetFields();
    if (initialValues) {
      let processedValues = { ...initialValues };
      if (typeof initialValues.characteristics === 'string') {
        try {
          processedValues.characteristics = JSON.parse(initialValues.characteristics);
        } catch (e) {
          console.error('Error parsing characteristics in AboutForm:', e);
          processedValues.characteristics = [];
        }
      }

      form.setFieldsValue(processedValues);
      setPreviews({
        director: initialValues.director_image || null,
        deputy_director: initialValues.deputy_director_image || null,
        assistant: initialValues.assistant_image || null,
        coordinator: initialValues.coordinator_image || null
      });
      setImageFiles({
        director: initialValues.director_image || null,
        deputy_director: initialValues.deputy_director_image || null,
        assistant: initialValues.assistant_image || null,
        coordinator: initialValues.coordinator_image || null
      });
    }
  }, [initialValues, form]);

  const handleImageChange = (role, info) => {
    if (info.file) {
      const isLt2M = info.file.size / 1024 / 1024 < 5;
      if (!isLt2M) {
        toast.error('Image must be smaller than 5MB!');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviews(prev => ({ ...prev, [role]: e.target.result }));
      };
      reader.readAsDataURL(info.file);
      setImageFiles(prev => ({ ...prev, [role]: info.file }));
    }
  };

  const removeImage = (role) => {
    setPreviews(prev => ({ ...prev, [role]: null }));
    setImageFiles(prev => ({ ...prev, [role]: 'remove' }));
  };

  const onFinish = async (values) => {
    try {
      const formData = new FormData();

      formData.append('title', values.title || '');
      formData.append('subtitle', values.subtitle || '');
      formData.append('whatis', values.whatis || '');
      formData.append('organization', values.organization || '');
      formData.append('characteristics', JSON.stringify(values.characteristics || []));
      formData.append('isActive', values.isActive ? "1" : "0");

      formData.append('director', values.director || '');
      formData.append('deputy_director', values.deputy_director || '');
      formData.append('assistant', values.assistant || '');
      formData.append('coordinator', values.coordinator || '');

      Object.keys(imageFiles).forEach(role => {
        if (imageFiles[role] instanceof File) {
          formData.append(`${role}_image`, imageFiles[role]);
        } else if (imageFiles[role] === 'remove') {
          formData.append(`${role}_image_remove`, '1');
        }
      });

      if (isEditing) {
        await dispatch(updateAboutContent({ id: initialValues.id, formData })).unwrap();
        toast.success('About content updated successfully');
      } else {
        await dispatch(createAboutContent(formData)).unwrap();
        toast.success('About content created successfully');
      }
      onSuccess();
      dispatch(resetSubmitStatus());
    } catch (error) {
      console.error('Error:', error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} content: ${error.message || error}`);
    }
  };

  return (
    <div className="about-form-wrapper">
      <div className="form-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          initialValues={initialValues || { characteristics: [{ value: '' }] }}
        >
          <Row gutter={[15, 15]}>
            <Col xs={24}>
              <Row gutter={[15, 0]} align="stretch">
                <Col xs={24} lg={12}>
                  <div className="premium-card card-indigo">
                    <div className="card-header-tag">General</div>
                    <Typography.Text className="section-label">General information</Typography.Text>
                    <Typography.Text className="section-description">Manage the core mission and introductory content of the page.</Typography.Text>

                    <Form.Item
                      name="title"
                      label="Mission Title"
                      rules={[{ required: true, message: 'Please enter title' }]}
                    >
                      <Input prefix={<GlobalOutlined />} placeholder="e.g. Empowering the Next Generation" />
                    </Form.Item>

                    <Form.Item
                      name="subtitle"
                      label="Introductory Subtitle"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <TextArea rows={5} placeholder="Brief summary for the top section..." />
                    </Form.Item>
                    <Form.Item
                      name="whatis"
                      label="Detailed Mission (What is)"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <TextArea rows={5} placeholder="In-depth explanation of the project..." />
                    </Form.Item>
                  </div>
                </Col>

                <Col xs={24} lg={12}>
                  <div className="premium-card card-orange">
                    <div className="card-header-tag">Features</div>
                    <Typography.Text className="section-label">Core characteristics</Typography.Text>
                    <Typography.Text className="section-description">Key values and features associated with the foundation.</Typography.Text>

                    <Form.List name="characteristics">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <div key={key} className="characteristic-row">
                              <Form.Item
                                {...restField}
                                name={[name, 'value']}
                                rules={[{ required: true, message: 'Required' }]}
                                style={{ margin: 0, flex: 1 }}
                              >
                                <Input prefix={<CheckCircleOutlined />} placeholder="e.g. Innovation" />
                              </Form.Item>
                              {fields.length > 1 && (
                                <Button
                                  type="text"
                                  danger
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => remove(name)}
                                  style={{ display: 'flex', alignItems: 'center' }}
                                />
                              )}
                            </div>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            style={{ marginTop: 12, height: '40px', borderRadius: '8px' }}
                          >
                            Add new characteristic
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </div>
                </Col>
              </Row>
            </Col>

            <Col xs={24}>
              {/* Card 2: Organization Highlights */}
              <div className="premium-card card-green" style={{ marginBottom: 0 }}>
                <div className="card-header-tag">Content</div>
                <Typography.Text className="section-label">Organization highlights</Typography.Text>
                <Typography.Text className="section-description" style={{ marginBottom: '16px' }}>Key structure and historical context.</Typography.Text>

                <Form.Item name="organization" style={{ marginBottom: 0 }} className="flex-form-item">
                  <TextArea rows={3} placeholder="Describe the organizational setup..." style={{ minHeight: '120px' }} />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24}>
              <div className="premium-card card-blue">
                <div className="card-header-tag">Team</div>
                <Typography.Text className="section-label">Leadership & Coordination</Typography.Text>
                <Typography.Text className="section-description">Manage the profiles and photos of the key leaders.</Typography.Text>

                <Row gutter={[24, 24]}>
                  {[
                    { name: 'director', label: 'Director' },
                    { name: 'deputy_director', label: 'Deputy Director' },
                    { name: 'assistant', label: 'Development Executive' },
                    { name: 'coordinator', label: 'Coordinator' }
                  ].map((role) => (
                    <Col xs={24} sm={12} xl={12} key={role.name}>
                      <div className="leadership-item">
                        <div className="avatar-upload-wrapper">
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={(file) => {
                              handleImageChange(role.name, { file });
                              return false;
                            }}
                          >
                            <div className="avatar-container">
                              <Avatar
                                size={80}
                                src={previews[role.name]}
                                icon={<UserOutlined />}
                                style={{ backgroundColor: 'var(--slate-100)', color: 'var(--slate-600)' }}
                              />
                              <div className="avatar-overlay">
                                <CameraOutlined style={{ fontSize: '16px' }} />
                              </div>
                            </div>
                          </Upload>
                          {previews[role.name] && (
                            <div className="avatar-delete-badge" onClick={() => removeImage(role.name)}>
                              <CloseOutlined style={{ fontSize: '10px' }} />
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            name={role.name}
                            label={role.label}
                            style={{ marginBottom: 0, }}
                          >
                            <Input prefix={<UserOutlined />} placeholder="Full name" />
                          </Form.Item>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Col>
          </Row>

          {/* Action Area */}
          {/* Action Area */}
          <div className="sticky-action-footer">
            <div style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Form.Item name="isActive" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
              <Typography.Text type="secondary" style={{ fontSize: '14px', fontWeight: 500 }}>
                Active status
              </Typography.Text>
            </div>

            <Button type="default" size="large" onClick={() => onSuccess()} style={{ borderRadius: '8px', height: '44px' }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitStatus === 'loading'}
              icon={isEditing ? <CheckCircleOutlined /> : <PlusOutlined />}
            >
              {isEditing ? 'Save changes' : 'Create profile'}
            </Button>
          </div>
          
          <div className="form-footer-spacer" />
        </Form>
      </div>
    </div >
  );
};

export default AboutForm;
